import * as store from '@attain-apps/fe/web/main/lib/store'
import * as rxjs from 'rxjs'

import * as ecModels from '../../../../models'
import type * as models from '../models'
import type { Plugin } from '../plugin'

/**
 * Executes an API {@link models.Request}
 */
export const executeRequest = store.effects.create(
  <TTypeParams extends ecModels.TypeParams.Constraint>(
    feature: Plugin<TTypeParams>['feature'],
    api: ecModels.Api<TTypeParams>,
  ) =>
    feature.actions.execute.request.observe().pipe(
      rxjs.mergeMap(action => {
        // NOTE: since request looks different for each method name, and there
        // are deferred type parameters involved, it is very difficult to
        // actually use action.data in this context, so we just cast to a plain
        // request
        const request = action.data as models.Request
        // First, we try to invoke the method to get the appropriate observable
        // on the results
        const observable = ecModels.Api._unsafeInvoke(
          api,
          request.methodName,
          // NOTE: we use this context rather than retrieving latest value of
          // context because doing so would open us up to the risk of context
          // changing right after a request is made (e.g. request to create
          // entity in context a is submitted, context changes to context b,
          // this effect tries to create entity in context b)
          request.context,
          ...(request.args ?? []),
        ) satisfies rxjs.Observable<unknown> | undefined as
          | rxjs.Observable<
              ecModels.Api.Method.Result<
                ecModels.Api.Method.Name,
                ecModels.Api<TTypeParams>
              >
            >
          | undefined
        if (!observable) {
          // If the observable does not exist, that means the method is not
          // implemented, which means we should cancel the request
          return rxjs.of(
            feature.actions.execute.cancellation.from(action).create({
              reason: `Method '${request.methodName}' does not exist on the API.`,
              request,
            }),
          )
        }
        return observable.pipe(
          // When we get a response, the request is successful
          rxjs.map(response =>
            feature.actions.execute.success.from(action).create({
              request,
              response,
            }),
          ),
          // If we get an error, the request has failed
          rxjs.catchError(error =>
            rxjs.of(
              feature.actions.execute.error.from(action).create({
                error,
                request,
              }),
            ),
          ),
        )
      }),
    ),
)
